import { useState, useEffect } from 'react';
import { Event } from '../lib/types';

interface Attendee {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  additionalAdults: number;
  children: number;
  totalGuests: number;
  publicGuestView: boolean;
}

interface AttendeeListModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
}

export default function AttendeeListModal({ isOpen, onClose, event }: AttendeeListModalProps) {
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMoreAttendees, setHasMoreAttendees] = useState(false);
  const [hiddenGuestCount, setHiddenGuestCount] = useState(0);

  useEffect(() => {
    const fetchAttendees = async () => {
      if (!isOpen || !event) return;
      
      try {
        setIsLoading(true);
        // Handle both id and objectId fields
        const eventId = event.id || (event as any).objectId;
        
        if (!eventId) {
          console.error('No event ID found for attendees');
          setAttendees([]);
          return;
        }
        
        const response = await fetch(`/api/events/${eventId}/attendees`);
        
        if (response.ok) {
          const data = await response.json();
          setAttendees(data.attendees || []);
          setHasMoreAttendees(data.hasMoreAttendees || false);
          setHiddenGuestCount(data.hiddenGuestCount || 0);
        } else {
          console.error('Failed to fetch attendees, status:', response.status);
          setAttendees([]);
        }
      } catch (error) {
        console.error('Error fetching attendees:', error);
        setAttendees([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttendees();
  }, [isOpen, event]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">See Who's In</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Attendee List */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : attendees.length > 0 ? (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  {attendees.reduce((total, attendee) => total + attendee.totalGuests, 0) + hiddenGuestCount} {attendees.reduce((total, attendee) => total + attendee.totalGuests, 0) + hiddenGuestCount === 1 ? 'guest is' : 'guests are'} attending this event
                  {hiddenGuestCount > 0 && (
                    <span className="block text-xs text-gray-400 mt-1">
                      {hiddenGuestCount} {hiddenGuestCount === 1 ? 'guest has' : 'guests have'} chosen not to display their name publicly
                    </span>
                  )}
                </p>
                <div className="space-y-3">
                  {attendees.map((attendee) => (
                    <div key={attendee.id} className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-medium">
                        ✓
                      </div>
                      <span className="text-gray-900 font-medium">{attendee.name}</span>
                    </div>
                  ))}
                </div>
                
                {/* Show limit message if there are more attendees */}
                {hasMoreAttendees && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                      Only 50 Guests Are Displayed
                    </p>
                  </div>
                )}
              </>
            ) : hiddenGuestCount > 0 ? (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  {hiddenGuestCount} {hiddenGuestCount === 1 ? 'guest is' : 'guests are'} attending this event
                  <span className="block text-xs text-gray-400 mt-1">
                    {hiddenGuestCount} {hiddenGuestCount === 1 ? 'guest has' : 'guests have'} chosen not to display their name publicly
                  </span>
                </p>
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 mb-2">Guests are attending but have chosen to remain private</p>
                  <p className="text-xs text-gray-400">Check back later to see who's coming!</p>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No one has RSVP'd yet</p>
                <p className="text-gray-400 text-xs mt-1">Be the first to join!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
